import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import TandMForm from "./TimeAndMaterialForm";
import { useState } from "react";
import axios from "axios";
import AlertDialog from "./AlertDialog";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import { saveAs } from "file-saver";
import ImageModule from "docxtemplater-image-module-free";

export default function TandMView({
  selectedEntry,
  companyProfile,
  availableLabourItems,
  availableMaterialItems,
  costCodeOptions,
  vendorOptions,
  bidAreaOptions,
  setViewMode,
  project,
  setTimeAndMaterials,
  setProject,
  formData,
  setFormData,
  selectedLabourItem,
  setSelectedLabourItem,
  selectedMaterialItem,
  setSelectedMaterialItem,
  labourQty,
  setLabourQty,
  materialQty,
  setMaterialQty,
  images,
  setImages,
  selectedVendor,
  setSelectedVendor,
  selectedLabourBidArea,
  setSelectedLabourBidArea,
  selectedMaterialAnticipatedBidArea,
  setSelectedMaterialAnticipatedBidArea,
  selectedLabourCostCode,
  setSelectedLabourCostCode,
  selectedMaterialCostCode,
  setSelectedMaterialCostCode,
  editingLabourItemIndex,
  setEditingLabourItemIndex,
  editingMaterialItemIndex,
  setEditingMaterialItemIndex,
  editLabourForm,
  setEditLabourForm,
  editMaterialForm,
  setEditMaterialForm,
  setIsCreating,
  setParentIsEditing,
  setEditingId,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    type: "",
    title: "",
    message: "",
  });

  const subtotalCost =
    selectedEntry.labourItems?.reduce(
      (sum, item) => sum + (item.budget ? item.totalCost || 0 : 0),
      0
    ) +
    selectedEntry.materialItems.reduce(
      (sum, item) => sum + (item.budget ? item.totalCost || 0 : 0),
      0
    );
  const subtotalPrice =
    selectedEntry.labourItems?.reduce(
      (sum, item) => sum + (item.customer ? item.totalPrice || 0 : 0),
      0
    ) +
    selectedEntry.materialItems.reduce(
      (sum, item) => sum + (item.customer ? item.totalPrice || 0 : 0),
      0
    );
  const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
  const taxAmount = subtotalPrice * (taxRate / 100);
  const totalPrice = subtotalPrice + taxAmount;

  const generateTandMPDF = async () => {
    if (!companyProfile) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Cannot generate PDF: Company profile is not set.",
      });
      return;
    }

    if (!selectedEntry) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "No entry available to generate PDF.",
      });
      return;
    }

    // Validate entry structure
    const entry = {
      ...selectedEntry,
      labourItems: Array.isArray(selectedEntry.labourItems)
        ? selectedEntry.labourItems
        : [],
      materialItems: Array.isArray(selectedEntry.materialItems)
        ? selectedEntry.materialItems
        : [],
      surchargeItems: Array.isArray(selectedEntry.surchargeItems)
        ? selectedEntry.surchargeItems
        : [],
    };

    try {
      const response = await fetch("/t_and_m_template.docx");
      if (!response.ok)
        throw new Error(`Failed to load template: ${response.statusText}`);
      const templateArrayBuffer = await response.arrayBuffer();
      const zip = new PizZip(templateArrayBuffer);

      const base64Regex =
        /^(?:data:)?image\/(png|jpg|jpeg|svg|svg\+xml);base64,/;
      const validBase64 =
        /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;

      function base64Parser(tagValue) {
        if (typeof tagValue !== "string" || !base64Regex.test(tagValue))
          return null;
        const stringBase64 = tagValue.replace(base64Regex, "");
        if (!validBase64.test(stringBase64))
          throw new Error("Invalid base64 data");
        const binaryString = window.atob(stringBase64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binaryString.charCodeAt(i);
        return bytes.buffer;
      }

      const imageOptions = {
        getImage: (tagValue) => base64Parser(tagValue),
        getSize: (img, tagValue, tagName) => {
          if (tagName === "companyLogo") return [150, 100];
          if (tagName === "signature") return [100, 150];
          if (tagName === "image") return [200, 200];
          return [150, 100];
        },
      };
      const imageModule = new ImageModule(imageOptions);

      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true,
        modules: [imageModule],
      });

      let companyLogoBase64 = null;
      if (
        companyProfile?.logo &&
        !companyProfile.logo.startsWith("data:image")
      ) {
        try {
          const logoResponse = await fetch(companyProfile.logo, {
            mode: "cors",
          });
          if (!logoResponse.ok)
            throw new Error(`Failed to fetch logo: ${logoResponse.statusText}`);
          const logoBlob = await logoResponse.blob();
          companyLogoBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
        } catch (err) {
          console.error("Error fetching company logo:", err.message);
          companyLogoBase64 = null;
        }
      } else if (companyProfile?.logo) {
        companyLogoBase64 = companyProfile.logo;
      }

      let signatureBase64 = null;
      if (entry.signature) {
        try {
          const sigResponse = await fetch(
            entry.signature instanceof File
              ? URL.createObjectURL(entry.signature)
              : entry.signature,
            { mode: "cors" }
          );
          if (!sigResponse.ok)
            throw new Error(
              `Failed to fetch signature: ${sigResponse.statusText}`
            );
          const sigBlob = await sigResponse.blob();
          signatureBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(sigBlob);
          });
        } catch (err) {
          console.warn("Failed to fetch signature:", err.message);
          signatureBase64 = null;
        }
      }

      const imagesPromises = (entry.images || []).map(async (url, index) => {
        let imageBase64 = null;
        try {
          const imgResponse = await fetch(
            url instanceof File ? URL.createObjectURL(url) : url,
            { mode: "cors" }
          );
          if (!imgResponse.ok)
            throw new Error(`Failed to fetch image: ${imgResponse.statusText}`);
          const imgBlob = await imgResponse.blob();
          imageBase64 = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(imgBlob);
          });
        } catch (err) {
          console.warn(`Failed to fetch image ${index + 1}: ${err.message}`);
          imageBase64 = null;
        }
        return { image: imageBase64 };
      });

      const resolvedImages = await Promise.all(imagesPromises);

      // Process labour items
      const labourItems = entry.labourItems
        .filter((item) => item && item.customer === true)
        .map((item, index) => {
          const labourOption = availableLabourItems.find(
            (opt) =>
              opt.value ===
              (item.contractLabourItem?._id ||
                item.contractLabourItem ||
                item.budgetLineItem?._id ||
                item.budgetLineItem)
          );
          const description = labourOption
            ? labourOption.label.split(" (")[0]
            : item.contractLabourItem?.labourItem?.title ||
              item.budgetLineItem?.costCodeDescription ||
              "Unnamed Labour Item";
          const unitPrice =
            item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
          return {
            itemNo: index + 1,
            description,
            unit: "HR",
            quantity: item.quantityUsed || 0,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: (item.totalPrice || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      // Process material items
      const materialItems = entry.materialItems
        .filter((item) => item && item.customer === true)
        .map((item, index) => {
          const materialOption = availableMaterialItems.find(
            (opt) =>
              opt.value === (item.budgetLineItem?._id || item.budgetLineItem)
          );
          const description = `${
            item.itemDescription || materialOption?.itemDescription || "Unnamed"
          } (${item.container || materialOption?.container || "N/A"} - ${
            item.size || materialOption?.size || "N/A"
          })`;
          const unitPrice =
            item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
          return {
            itemNo: labourItems.length + index + 1,
            description,
            unit: item.container,
            quantity: item.quantityUsed || 0,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: (item.totalPrice || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      // Process surcharge items
      const surchargeItems = entry.surchargeItems
        .filter((item) => item && item.customer === true)
        .map((item, index) => {
          const unitPrice =
            item.quantityUsed > 0 ? item.totalPrice / item.quantityUsed : 0;
          return {
            itemNo: labourItems.length + materialItems.length + index + 1,
            description: item.title || "Unnamed Surcharge",
            quantity: item.quantityUsed || 0,
            price: unitPrice.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
            amount: (item.totalPrice || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          };
        });

      const items = [...labourItems, ...materialItems, ...surchargeItems];

      if (items.length === 0) {
        setAlert({
          open: true,
          type: "warning",
          title: "Warning",
          message: "No customer-facing items to include in the PDF.",
        });
        return;
      }

      // Calculate financials
      const subtotalPrice = items.reduce((sum, item) => {
        const amount = parseFloat(item.amount.replace(/[^0-9.-]+/g, "")) || 0;
        return sum + amount;
      }, 0);
      const taxRate = parseFloat(companyProfile?.defaultTax?.taxRate) || 0;
      const taxAmount = subtotalPrice * (taxRate / 100);
      const totalPrice = subtotalPrice + taxAmount;

      const data = {
        projectName: project.projectName || "Unknown Project",
        date: new Date(entry.signedAt || Date.now()).toLocaleDateString(),
        preparedBy: entry.createdBy?.displayName || "Unknown",
        timeAndMaterialNumber: entry.timeAndMaterialNumber || "Draft",
        scopeOfWork: entry.scopeOfWork || "N/A",
        signedBy: entry.signedBy || "N/A",
        companyName: companyProfile?.companyName || "Your Company Name",
        companyAddress: companyProfile?.address
          ? `${companyProfile.address.street}, ${companyProfile.address.city}, ${companyProfile.address.state} ${companyProfile.address.zip}, ${companyProfile.address.country}`
          : "N/A",
        companyLogo: companyLogoBase64 || null,
        signature: signatureBase64 || null,
        items,
        pageBreak: "\f", // REQUIRED
        images: resolvedImages,
        subtotalPrice: subtotalPrice.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        taxRate: taxRate.toFixed(2),
        taxAmount: taxAmount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        totalPrice: totalPrice.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        }),
        overheadPercentage: entry.overheadPercentage
          ? entry.overheadPercentage.toFixed(2)
          : "0.00",
      };

      doc.setData(data);
      try {
        doc.render();
      } catch (renderError) {
        console.error("Error rendering Docxtemplater:", renderError);
        throw new Error("Failed to render document template.");
      }

      const docxBlob = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });

      const formDataPDF = new FormData();
      formDataPDF.append(
        "docx",
        docxBlob,
        `TandM_${entry._id || "draft"}.docx`
      );
      formDataPDF.append("documentType", "TandM");
      formDataPDF.append("identifier", entry._id || "draft");

      const token = localStorage.getItem("token");
      const pdfResponse = await axios.post(
        "https://erp-production-72da01c8e651.herokuapp.com/api/upload/convert-to-pdf",
        formDataPDF,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        }
      );

      if (
        pdfResponse.status === 200 &&
        pdfResponse.headers["content-type"] === "application/pdf"
      ) {
        const pdfBlob = new Blob([pdfResponse.data], {
          type: "application/pdf",
        });
        saveAs(pdfBlob, `TandM_${entry.timeAndMaterialNumber || "draft"}.pdf`);
        setAlert({
          open: true,
          type: "success",
          title: "Success",
          message: "T&M PDF generated successfully!",
        });
      } else {
        throw new Error("Failed to convert DOCX to PDF");
      }
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to generate PDF: " + err.message,
      });
      console.error("Error in generateTandMPDF:", err);
    }
  };

  const handleEdit = () => {
    try {
      if (!selectedEntry) {
        throw new Error("No T&M entry selected");
      }
      if (!Array.isArray(selectedEntry.labourItems)) {
        console.warn(
          "selectedEntry.labourItems is not an array:",
          selectedEntry.labourItems
        );
        selectedEntry.labourItems = [];
      }
      if (!Array.isArray(selectedEntry.materialItems)) {
        console.warn(
          "selectedEntry.materialItems is not an array:",
          selectedEntry.materialItems
        );
        selectedEntry.materialItems = [];
      }

      const labourItems = selectedEntry.labourItems
        .map((item) => {
          if (!item) {
            console.warn("Invalid labour item encountered:", item);
            return null;
          }
          return {
            contractLabourItem:
              item.contractLabourItem &&
              typeof item.contractLabourItem === "object" &&
              item.contractLabourItem._id
                ? item.contractLabourItem._id
                : item.contractLabourItem || null,
            budgetLineItem:
              item.budgetLineItem &&
              typeof item.budgetLineItem === "object" &&
              item.budgetLineItem._id
                ? item.budgetLineItem._id
                : item.budgetLineItem || null,
            quantityUsed: parseFloat(item.quantityUsed) || 0,
            totalPrice: parseFloat(item.totalPrice) || 0,
            totalCost: parseFloat(item.totalCost) || 0,
            bidArea: item.bidArea || "",
            costCode:
              item.costCode &&
              typeof item.costCode === "object" &&
              item.costCode._id
                ? item.costCode._id
                : item.costCode || null,
            customer: item.customer ?? true,
            budget: item.budget ?? true,
            unitRate: parseFloat(item.unitRate) || 0,
            uom: item.uom || "N/A",
          };
        })
        .filter((item) => item !== null);

      const materialItems = selectedEntry.materialItems
        .map((item) => {
          if (!item) {
            console.warn("Invalid material item encountered:", item);
            return null;
          }
          const materialOption = availableMaterialItems.find(
            (opt) =>
              opt.value ===
              (typeof item.budgetLineItem === "object" &&
              item.budgetLineItem?._id
                ? item.budgetLineItem._id
                : item.budgetLineItem)
          );
          return {
            budgetLineItem:
              item.budgetLineItem &&
              typeof item.budgetLineItem === "object" &&
              item.budgetLineItem._id
                ? item.budgetLineItem._id
                : item.budgetLineItem || null,
            quantityUsed: parseFloat(item.quantityUsed) || 0,
            totalCost: parseFloat(item.totalCost) || 0,
            totalPrice: parseFloat(item.totalPrice) || 0,
            price: parseFloat(item.price) || 0,
            cost: parseFloat(item.cost) || 0,
            size: item.size || materialOption?.size || "N/A",
            itemCode: item.itemCode || materialOption?.itemCode || "N/A",
            itemDescription:
              item.itemDescription ||
              materialOption?.itemDescription ||
              "Unnamed",
            container: item.container || materialOption?.container || "N/A",
            vendor:
              item.vendor && typeof item.vendor === "object" && item.vendor._id
                ? item.vendor._id
                : item.vendor || null,
            bidArea: item.bidArea || "",
            costCode:
              item.costCode &&
              typeof item.costCode === "object" &&
              item.costCode._id
                ? item.costCode._id
                : item.costCode || null,
            customer: item.customer ?? true,
            budget: item.budget ?? true,
          };
        })
        .filter((item) => item !== null);

      setFormData({
        labourItems,
        materialItems,
        scopeOfWork: selectedEntry.scopeOfWork || "",
        signature: selectedEntry.signature || null,
        signedBy: selectedEntry.signedBy || "",
        overheadPercentage: parseFloat(selectedEntry.overheadPercentage) || 0,
      });
      setImages(
        selectedEntry.images && Array.isArray(selectedEntry.images)
          ? selectedEntry.images
          : []
      );
      setIsEditing(true);
      setEditingId(selectedEntry._id);
      setIsCreating(true);
      setParentIsEditing(true);
    } catch (err) {
      console.error("Error initializing edit mode:", err.message, err.stack);
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: `Failed to load entry for editing: ${err.message}`,
      });
    }
  };

  const handleApprove = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material/${project._id}/${selectedEntry._id}/status`,
        { status: "Approved" },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTimeAndMaterials((prev) =>
        prev.map((item) =>
          item._id === selectedEntry._id
            ? { ...item, status: "Approved", signedAt: new Date() }
            : item
        )
      );
      setAlert({
        open: true,
        type: "success",
        title: "Success",
        message: "T&M entry approved successfully!",
      });
      setViewMode("list");
    } catch (err) {
      setAlert({
        open: true,
        type: "error",
        title: "Error",
        message: "Failed to approve T&M entry.",
      });
      console.error("Error approving T&M entry:", err);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setEditingId(null);
    setParentIsEditing(false);
    setFormData({
      labourItems: [],
      materialItems: [],
      scopeOfWork: "",
      signature: null,
      signedBy: "",
      overheadPercentage: companyProfile?.defaultOverhead || 0,
    });
    setSelectedLabourItem(null);
    setSelectedMaterialItem(null);
    setLabourQty("");
    setMaterialQty("");
    setSelectedVendor(null);
    setSelectedLabourBidArea(null);
    setSelectedMaterialAnticipatedBidArea(null);
    setSelectedLabourCostCode(null);
    setSelectedMaterialCostCode(null);
    setImages([]);
    setEditingLabourItemIndex(null);
    setEditingMaterialItemIndex(null);
    setEditLabourForm({
      contractLabourItem: null,
      quantityUsed: "",
      bidArea: null,
      costCode: null,
      customer: true,
      budget: true,
    });
    setEditMaterialForm({
      budgetLineItem: null,
      quantityUsed: "",
      vendor: null,
      bidArea: null,
      costCode: null,
      customer: true,
      budget: true,
    });
  };

  if (isEditing) {
    return (
      <TandMForm
        isEditing={true}
        editingId={selectedEntry._id}
        formData={formData}
        setFormData={setFormData}
        availableLabourItems={availableLabourItems}
        availableMaterialItems={availableMaterialItems}
        selectedLabourItem={selectedLabourItem}
        setSelectedLabourItem={setSelectedLabourItem}
        selectedMaterialItem={selectedMaterialItem}
        setSelectedMaterialItem={setSelectedMaterialItem}
        labourQty={labourQty}
        setLabourQty={setLabourQty}
        materialQty={materialQty}
        setMaterialQty={setMaterialQty}
        images={images}
        setImages={setImages}
        selectedVendor={selectedVendor}
        setSelectedVendor={setSelectedVendor}
        selectedLabourBidArea={selectedLabourBidArea}
        setSelectedLabourBidArea={setSelectedLabourBidArea}
        selectedMaterialAnticipatedBidArea={selectedMaterialAnticipatedBidArea}
        setSelectedMaterialAnticipatedBidArea={
          setSelectedMaterialAnticipatedBidArea
        }
        selectedLabourCostCode={selectedLabourCostCode}
        setSelectedLabourCostCode={setSelectedLabourCostCode}
        selectedMaterialCostCode={selectedMaterialCostCode}
        setSelectedMaterialCostCode={setSelectedMaterialCostCode}
        bidAreaOptions={bidAreaOptions}
        editingLabourItemIndex={editingLabourItemIndex}
        setEditingLabourItemIndex={setEditingLabourItemIndex}
        editingMaterialItemIndex={editingMaterialItemIndex}
        setEditingMaterialItemIndex={setEditingMaterialItemIndex}
        editLabourForm={editLabourForm}
        setEditLabourForm={setEditLabourForm}
        editMaterialForm={editMaterialForm}
        setEditMaterialForm={setEditMaterialForm}
        handleCreateToggle={handleCancelEdit}
        companyProfile={companyProfile}
        preparedBy={selectedEntry.createdBy?.displayName || ""}
        setAlert={setAlert}
        vendorOptions={vendorOptions}
        costCodeOptions={costCodeOptions}
        subtotalCost={subtotalCost}
        subtotalPrice={subtotalPrice}
        taxRate={taxRate}
        taxAmount={taxAmount}
        totalPrice={totalPrice}
        project={project}
        setTimeAndMaterials={setTimeAndMaterials}
        setProject={setProject}
        setIsCreating={setIsCreating}
        setIsEditing={setParentIsEditing}
        setEditingId={setEditingId}
        setViewMode={setViewMode}
      />
    );
  }

  return (
    <div className="bg-gray-700 rounded-lg shadow-md border border-gray-600 p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white tracking-tight">
          View T&M Entry - {selectedEntry?.status || "N/A"}
        </h2>
        <div className="space-x-3">
          <button
            onClick={() => setViewMode("list")}
            className="inline-flex px-4 py-2 text-sm font-medium text-gray-200 bg-gray-600 rounded-md hover:bg-gray-500 transition-colors duration-200 shadow-sm"
          >
            <ArrowLeftIcon className="size-4 mr-2" />
            Back to List
          </button>
          {selectedEntry?.status === "Rejected" && (
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-sm font-medium text-gray-200 bg-blue-400 rounded-md hover:bg-blue-500 transition-colors duration-200 shadow-sm"
            >
              Edit
            </button>
          )}
          <button
            onClick={generateTandMPDF}
            className={`px-4 py-2 text-sm font-medium text-gray-200 rounded-md transition-colors duration-200 shadow-sm ${
              selectedEntry &&
              (selectedEntry.labourItems?.length > 0 ||
                selectedEntry.materialItems?.length > 0)
                ? "bg-orange-400 hover:bg-orange-500"
                : "bg-gray-500 cursor-not-allowed"
            }`}
            disabled={
              !selectedEntry ||
              (!selectedEntry.labourItems?.length &&
                !selectedEntry.materialItems?.length)
            }
          >
            Download PDF
          </button>
        </div>
      </div>

      <div className="bg-gray-700 p-6 rounded-lg border border-gray-600">
        <div className="space-y-6 overflow-x-auto">
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">
              Labour Items
            </h3>
            {selectedEntry.labourItems &&
            selectedEntry.labourItems.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Budget
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Item
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Cost Code
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Cost
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Bid Area
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-600">
                  {selectedEntry.labourItems.map((item, index) => (
                    <tr
                      key={index}
                      className="hover:bg-gray-500 transition-colors duration-200"
                    >
                      <td className="px-4 py-3 text-sm text-gray-200">
                        {item.customer ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-200">
                        {item.budget ? "Yes" : "No"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-200">
                        {item.contractLabourItem?.labourItem?.title ||
                          availableLabourItems
                            .find(
                              (opt) =>
                                opt.value ===
                                (item.contractLabourItem?._id ||
                                  item.budgetLineItem)
                            )
                            ?.label.split(" (")[0] ||
                          item.budgetLineItem?.costCodeDescription ||
                          "Unnamed"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-200">
                        {costCodeOptions.find(
                          (opt) =>
                            opt.value === (item.costCode?._id || item.costCode)
                        )?.label || "Unassigned"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-200">
                        {item.quantityUsed}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-200">
                        ${item.totalCost ? item.totalCost.toFixed(2) : "0.00"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-200">
                        ${item.totalPrice ? item.totalPrice.toFixed(2) : "0.00"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-200">
                        {item.bidArea || "Unassigned"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-gray-400">No labour items.</p>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">
              Material Items
            </h3>
            {selectedEntry.materialItems &&
            selectedEntry.materialItems.length > 0 ? (
              <div>
                <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Budget
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Cost Code
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Size
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Container
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Qty
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Cost Per
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Price Per
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Vendor
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                        Bid Area
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-600">
                    {selectedEntry.materialItems.map((item, index) => {
                      const materialOption = availableMaterialItems.find(
                        (opt) =>
                          opt.value ===
                          (item.budgetLineItem?._id || item.budgetLineItem)
                      );
                      return (
                        <tr
                          key={index}
                          className="hover:bg-gray-500 transition-colors duration-200"
                        >
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.customer ? "Yes" : "No"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.budget ? "Yes" : "No"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.itemDescription ||
                              materialOption?.itemDescription ||
                              "Unnamed"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {costCodeOptions
                              .find(
                                (opt) =>
                                  opt.value ===
                                  (item.costCode?._id || item.costCode)
                              )
                              ?.label.slice(0, 40) || "Unassigned"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.size || materialOption?.size || "N/A"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.container ||
                              materialOption?.container ||
                              "N/A"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.quantityUsed}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            ${item.cost ? item.cost.toFixed(2) : "0.00"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            $
                            {item.quantityUsed
                              ? (item.totalPrice / item.quantityUsed).toFixed(2)
                              : "0.00"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.vendor &&
                            typeof item.vendor === "object" &&
                            item.vendor.companyName
                              ? item.vendor.companyName
                              : vendorOptions.find(
                                  (opt) =>
                                    opt.value ===
                                    (item.vendor?._id || item.vendor)
                                )?.label || "Unassigned"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-200">
                            {item.bidArea || "Unassigned"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mt-4 flex justify-end space-x-4">
                  <div className="text-sm font-medium text-gray-200">
                    Subtotal Cost:{" "}
                    <span className="font-bold">
                      ${subtotalCost.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-200">
                    Subtotal Price (w/o OH):{" "}
                    <span className="font-bold">
                      ${subtotalPrice.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-gray-200">
                    Tax ({taxRate}%):{" "}
                    <span className="font-bold">${taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="text-sm font-medium text-gray-200">
                    Total Price:{" "}
                    <span className="font-bold">${totalPrice.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400">No material items.</p>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">
              Overhead Percentage
            </h3>
            <p className="text-sm text-gray-200">
              {selectedEntry.overheadPercentage
                ? selectedEntry.overheadPercentage.toFixed(2)
                : "0.00"}
              %
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">
              Scope of Work
            </h3>
            <p className="text-sm text-gray-200">
              {selectedEntry.scopeOfWork || "N/A"}
            </p>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Images</h3>
            {selectedEntry.images && selectedEntry.images.length > 0 ? (
              <div className="grid grid-cols-3 gap-4">
                {selectedEntry.images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Image ${index}`}
                    className="w-full h-24 object-cover rounded-lg border border-gray-600"
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No images.</p>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Signature</h3>
            {selectedEntry.signature ? (
              <img
                src={selectedEntry.signature}
                alt="Signature"
                className="w-[100px] h-[150px] object-contain rounded-lg border border-gray-600"
              />
            ) : (
              <p className="text-sm text-gray-400">No signature.</p>
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">
              Approved By
            </h3>
            <p className="text-sm text-gray-200">
              {selectedEntry.signedBy || "N/A"}
            </p>
          </div>
        </div>
      </div>
      <AlertDialog
        open={alert.open}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        confirmText="OK"
        onClose={() =>
          setAlert({ open: false, type: "", title: "", message: "" })
        }
        onConfirm={() =>
          setAlert({ open: false, type: "", title: "", message: "" })
        }
      />
    </div>
  );
}
